import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-11088">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-11089">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-11090">
        <div className="flex items-center gap-4" data-id="element-11091">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11092">
            <ArrowLeftIcon size={18} data-id="element-11093" />
          </button>
          <div data-id="element-11094">
            <div className="flex items-center gap-3" data-id="element-11095">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-11096">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-11097">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-11098">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11099">
            <MailIcon size={16} data-id="element-11100" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11101">
            <DownloadIcon size={16} data-id="element-11102" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11103">
            <CheckCircleIcon size={16} data-id="element-11104" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-11105">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11106">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-11107" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-11108" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11109">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11110">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-11111" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11112">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-11113" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-11114" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-11115">
                    <TrashIcon size={16} className="mr-3" data-id="element-11116" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11117">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11118">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11119">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-11120">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11121">
                <PackageIcon size={18} className="text-gray-500" data-id="element-11122" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11123">
              <div className="space-y-4" data-id="element-11124">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-11125">
                    <div data-id="element-11126">
                      <div className="font-medium text-gray-900" data-id="element-11127">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-11128">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-11129">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-11130">
                <div className="flex justify-between text-sm" data-id="element-11131">
                  <span className="text-gray-500" data-id="element-11132">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11133">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11134">
                  <span className="text-gray-500" data-id="element-11135">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11136">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11137">
                  <span className="text-gray-500" data-id="element-11138">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11139">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-11140">
                  <span className="text-gray-900" data-id="element-11141">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#B38A00]" data-id="element-11142">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11143">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11144">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11145">
                <ClockIcon size={18} className="text-gray-500" data-id="element-11146" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11147">
              <div className="space-y-6" data-id="element-11148">
                <div className="flex items-start" data-id="element-11149">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11150">
                    <CheckIcon size={14} className="text-green-600" data-id="element-11151" />
                  </div>
                  <div className="ml-4" data-id="element-11152">
                    <p className="text-sm font-medium text-gray-900" data-id="element-11153">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-11154">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-11155">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-11156">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-11157" />
                    </div>
                    <div className="ml-4" data-id="element-11158">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11159">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11160">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11161">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11162">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11163">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11164">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11165">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11166">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-11167">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11168">
                      <CheckIcon size={14} className="text-green-600" data-id="element-11169" />
                    </div>
                    <div className="ml-4" data-id="element-11170">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11171">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11172">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11173">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11174">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11175">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11176">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11177">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11178">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-11179">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11180">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11181">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11182">
                <UserIcon size={18} className="text-gray-500" data-id="element-11183" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11184">
              <div className="flex items-center gap-3 mb-2" data-id="element-11185">
                <div className="w-10 h-10 bg-[#FFC70A]/10 rounded-full flex items-center justify-center text-[#B38A00] font-semibold" data-id="element-11186">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-11187">
                  <div className="font-medium text-gray-900" data-id="element-11188">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11189">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-11190">
                <div className="flex items-start gap-3" data-id="element-11191">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11192" />
                  <div data-id="element-11193">
                    <div className="text-sm text-gray-900" data-id="element-11194">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11195">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-11196">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11197" />
                  <div data-id="element-11198">
                    <div className="text-sm text-gray-900" data-id="element-11199">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11200">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-11201">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11202" />
                    <div data-id="element-11203">
                      <div className="text-sm text-gray-900" data-id="element-11204">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11205">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-11206">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11207" />
                  <div data-id="element-11208">
                    <div className="text-sm text-gray-900" data-id="element-11209">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-11210">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-11211">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11212">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11213">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11214">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-11215" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11216">
              <div className="flex items-start gap-3" data-id="element-11217">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11218" />
                <div data-id="element-11219">
                  <div className="text-sm text-gray-900" data-id="element-11220">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11221">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-11222">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11223" />
                <div data-id="element-11224">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-11225">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11226">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-11227">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11228" />
                  <div data-id="element-11229">
                    <div className="text-sm text-gray-900 break-all" data-id="element-11230">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11231">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-11232">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-11233" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-11234" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-11235" />}
                  <div data-id="element-11236">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-11237">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11238">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11239">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11240">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11241">
                <TruckIcon size={18} className="text-gray-500" data-id="element-11242" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11243">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-11244">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-11245">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-11246">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-11247">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}